//
// DISCLAIMER
//
// Copyright 2020-2026 ArangoDB GmbH, Cologne, Germany
//

import React, { Component, useEffect, useState } from "react";
import { Loader, Popup, Table, Menu, Button, Dropdown } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { Event as ApiEvent, EventList as ApiEventList, ListEventOptions as ApiListEventOptions, DataClusterAssignment } from "../../api/lib";
import { RouteComponentProps } from "react-router-dom";
import { withRefresh, IWithRefreshProps } from "../../util/WithRefresh";
import { ErrorMessage, Loading, LoaderBox, PagingButtons, UserLink, FlexBox } from "../../ui/lib";
import { hasSupportPermission, Permission } from "../../util/PermissionCache";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { CopyToClipboard } from "react-copy-to-clipboard";

// Single source of truth for the "Default" events filter. The events API filters
// only by an allow-list of types (there is no "exclude"), so each view enumerates
// the meaningful types and omits high-frequency noise such as data.deployment.billing
// (usage-item reconciliation, fired every few hours) — that has its own "Billing" filter.

// Organization overview: org-wide lifecycle topics (not subject-scoped).
const ORG_DEFAULT_EVENT_TYPES = [
  "resourcemanager.organization.created",
  "resourcemanager.organization.updated",
  "resourcemanager.organization.deleted",
  "resourcemanager.project.created",
  "resourcemanager.project.deleted",
  "iam.policy.updated",
  "data.deployment.created",
  "data.deployment.deleted",
  "credit.creditbundle.created",
  "credit.creditbundle.updated",
  "credit.creditbundle.deleted",
  "prepaid.prepaiddeployment.created",
  "prepaid.prepaiddeployment.updated",
  "prepaid.prepaiddeployment.deleted",
  "prepaid.prepaiddeployment.deployment-attached",
  "prepaid.prepaiddeployment.deployment-detached",
];

// Deployment Events tab: event types whose subject is the deployment id,
// excluding data.deployment.billing.
const DEPLOYMENT_DEFAULT_EVENT_TYPES = [
  "data.deployment.created",
  "data.deployment.updated",
  "data.deployment.deleted",
  "data.deployment.created-test-database",
  "data.deploymentcredentials.read",
  "replication.deploymentreplication.created",
  "replication.deploymentreplication.updated",
  "replication.deploymentreplication.deleted",
  "replication.deploymentmigration.created",
  "replication.deploymentmigration.updated",
];

interface IHeaderViewArgs {
  loading: boolean;
  page: number;
  count: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
}

const HeaderView = ({ ...args }: IHeaderViewArgs) => (
  <Table.Header>
    <Table.Row>
      <Table.HeaderCell>ID</Table.HeaderCell>
      <Table.HeaderCell>Type</Table.HeaderCell>
      <Table.HeaderCell>Organization</Table.HeaderCell>
      <Table.HeaderCell>Subject</Table.HeaderCell>
      <Table.HeaderCell>Created</Table.HeaderCell>
      <Table.HeaderCell>Created By</Table.HeaderCell>
      <Table.HeaderCell>Created With</Table.HeaderCell>
      <Table.HeaderCell>Reason</Table.HeaderCell>
      <Table.HeaderCell>
        <PagingButtons {...args} />
        <LoaderBox>
          <Loader size="mini" active={args.loading} inline />
        </LoaderBox>
      </Table.HeaderCell>
    </Table.Row>
  </Table.Header>
);

interface IRowView {
  item: ApiEvent;
}

const RowView = ({ ...args }: IRowView) => {
  const [hasCopiedDataClusterId, setHasCopiedDataClusterId] = React.useState(false);
  const [dataClusterId, setDataClusterId] = useState<DataClusterAssignment["datacluster_id"] | undefined>();
  const [isLoadingDataClusterId, setIsLoadingDataClusterId] = useState<boolean>(false);
  const evt = args.item;

  const onCopyDataClusterId = () => {
    setHasCopiedDataClusterId(true);
    setInterval(() => {
      setHasCopiedDataClusterId(false);
    }, 3000);
  };

  const { subject_id, subject_url } = evt;
  const isDeploymentRelated = String(subject_url).toLowerCase().includes("deployment");

  const getDataClusterId = async () => {
    setIsLoadingDataClusterId(true);
    try {
      const deploymentInfo = await apiClients.idashboardClient.GetDeployment({ id: subject_id });

      if (deploymentInfo.assignment?.datacluster_id) {
        setDataClusterId(deploymentInfo.assignment.datacluster_id);
      }
    } catch {
      setDataClusterId(undefined);
    } finally {
      setIsLoadingDataClusterId(false);
    }
  };

  useEffect(() => {
    if (isDeploymentRelated) {
      getDataClusterId();
    }
  }, []);

  return (
    <Table.Row>
      <Table.Cell>
        <div>{evt.id}</div>
      </Table.Cell>
      <Table.Cell>{evt.type || "-"}</Table.Cell>
      <Table.Cell>
        <div>{evt.organization_id || "-"}</div>
      </Table.Cell>
      <Table.Cell>
        <Popup
          hoverable
          trigger={<div>{evt.subject_id || "-"}</div>}
          content={
            <div>
              <p>{evt.subject_url || "-"}</p>
              {isDeploymentRelated &&
                (isLoadingDataClusterId ? (
                  <Loader active inline />
                ) : (
                  <FlexBox align="center">
                    <span>Data Cluster Id: {dataClusterId || "-"}</span>
                    <CopyToClipboard text={dataClusterId || "-"} onCopy={onCopyDataClusterId}>
                      <Button icon={hasCopiedDataClusterId ? "check" : "copy"} floated="right" size="tiny" basic />
                    </CopyToClipboard>
                  </FlexBox>
                ))}
            </div>
          }
        />
      </Table.Cell>
      <Table.Cell>{evt.created_at ? <DateTimePopupWithUTCAndLocalTime dateTime={evt.created_at} label="Created at" /> : "-"}</Table.Cell>
      <Table.Cell>{(evt.created_by_id || "").includes("oasis-system") ? evt.created_by_id : <UserLink id={evt.created_by_id || ""} />}</Table.Cell>
      <Table.Cell>{evt.created_with || "?"}</Table.Cell>
      <Table.Cell>{evt.reason || ""}</Table.Cell>
    </Table.Row>
  );
};

// Interface describing the list
interface IListView {
  items: ApiEvent[];
  loading: boolean;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
}

const ListView = ({ ...args }: IListView) => (
  <Table striped>
    <HeaderView {...args} count={args.items.length} />
    <Table.Body>
      {args.items.map((item) => {
        const id = item.id || "";
        return <RowView {...args} key={id} item={item} />;
      })}
    </Table.Body>
  </Table>
);

const EmptyView = () => <div>No events</div>;

// Interface describing the list view arguments
export interface IEventListViewArgs extends RouteComponentProps {
  loading: boolean;
  list?: ApiEventList;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
}

export const EventListView = ({ ...args }: IEventListViewArgs) => {
  if (!args.list) {
    return <Loading />;
  }
  const isEmpty = !args.list.items || args.list.items.length === 0;
  const items = args.list ? args.list.items : undefined;
  return (
    <div>
      <ListView {...args} items={items || []} />
      {isEmpty && <EmptyView />}
    </div>
  );
};

interface IEventListProps extends IWithRefreshProps, RouteComponentProps {
  organization_id?: string;
  subject_id?: string;
  // Selects the subject-scoped "Default" type allow-list (see DEPLOYMENT_DEFAULT_EVENT_TYPES).
  // "Billing" and "All" modes ignore it. Omit it (e.g. the project tab) to show every type.
  subjectKind?: "deployment";
}

interface IFilterViewArgs {
  filterMode: "default" | "all" | "billing";
  onFilterModeChanged: (filterMode: "default" | "all" | "billing") => void;
}

const FilterView = ({ ...args }: IFilterViewArgs) => {
  const filterOptions = [
    {
      key: "default",
      text: "Default",
      value: "default",
    },
    {
      key: "billing",
      text: "Billing",
      value: "billing",
    },
    {
      key: "all",
      text: "All",
      value: "all",
    },
  ];

  const filterLabels: Record<"default" | "all" | "billing", string> = {
    default: "Default",
    billing: "Billing",
    all: "All",
  };

  return (
    <Menu borderless pointing stackable>
      <Menu.Item header>Filter</Menu.Item>
      <Dropdown
        item
        value={args.filterMode}
        text={`Events: ${filterLabels[args.filterMode]}`}
        options={filterOptions}
        onChange={(e, d) => args.onFilterModeChanged(d.value as "default" | "all" | "billing")}
      />
    </Menu>
  );
};

interface IEventListState {
  list?: ApiEventList;
  errorMessage?: string;
  page: number;
  pageSize: number;
  filterMode: "default" | "all" | "billing";
}

// The component to show Events as a list.
class EventList extends Component<IEventListProps, IEventListState> {
  state: IEventListState = {
    list: undefined,
    errorMessage: undefined,
    page: 0,
    pageSize: 25,
    filterMode: "default",
  };

  hasPermission = (permission: Permission) => hasSupportPermission(permission, this.props.hasPermissionByUrl);

  reloadEvents = async () => {
    var req: ApiListEventOptions = {
      options: {
        page: this.state.page,
        page_size: this.state.pageSize,
      },
      sort_descending: true,
    };
    if (!!this.props.organization_id && req.options) {
      req.options.context_id = this.props.organization_id;
    }
    const isOrganizationOverview = !!this.props.organization_id && this.props.organization_id == this.props.subject_id;

    // On a subject-scoped page (deployment/project) always restrict to that
    // subject, in every mode. Only the type filter below varies per mode, so
    // "All" means "all event types for this subject" — not org-wide.
    if (!!this.props.subject_id && !isOrganizationOverview) {
      req.subject_ids = [this.props.subject_id];
    }

    if (this.state.filterMode === "billing") {
      // Billing group: only the dedicated usage-item-reconciliation events.
      req.types = ["data.deployment.billing"];
    } else if (this.state.filterMode === "default") {
      if (isOrganizationOverview) {
        // Organization level: do not filter on subject ID, instead filter on
        // types (aka topics), including project and deployment created/deleted.
        req.types = ORG_DEFAULT_EVENT_TYPES;
      } else if (this.props.subjectKind === "deployment") {
        // Deployment tab: curated set that keeps the high-frequency
        // data.deployment.billing reconciliation events out of the Default view.
        // The "Billing" and "All" modes ignore this and show those events.
        req.types = DEPLOYMENT_DEFAULT_EVENT_TYPES;
      }
    }
    // "all" mode: no type filter (the subject filter above still applies).
    const list = await apiClients.idashboardClient.ListAllEvents(req);
    this.setState({
      list: list,
    });
  };

  refreshEvents = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadEvents);
  };

  componentDidMount() {
    this.refreshEvents();
  }

  onNextPage = () => {
    this.setState(
      (prev) => ({
        page: prev.page + 1,
      }),
      this.refreshEvents
    );
  };

  onPreviousPage = () => {
    this.setState(
      (prev) => ({
        page: prev.page - 1,
      }),
      this.refreshEvents
    );
  };

  setFilterMode = (filterMode: "default" | "all" | "billing") => {
    this.setState(
      {
        filterMode,
        page: 0,
      },
      this.refreshEvents
    );
  };

  render() {
    const list = this.state.list;
    const showFilterToggle = !!this.props.organization_id;

    const eventList = <EventListView {...this.props} {...this.state} list={list} onNextPage={this.onNextPage} onPreviousPage={this.onPreviousPage} />;
    const filterView = showFilterToggle ? <FilterView filterMode={this.state.filterMode} onFilterModeChanged={this.setFilterMode} /> : null;

    return (
      <>
        <ErrorMessage active={!!this.state.errorMessage} message={this.state.errorMessage} />
        {filterView}
        {eventList}
      </>
    );
  }
}

export default withRefresh()(EventList);
